import { SEOHandle } from "@nasa-gcn/remix-seo";
import {
  ActionFunctionArgs,
  defer,
  LoaderFunctionArgs,
  MetaFunction,
  redirect,
} from "@remix-run/node";
import {
  Link,
  useFetcher,
  useLoaderData,
  useLocation,
  useSearchParams,
} from "@remix-run/react";
import isEmpty from "lodash/isEmpty";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import { getFormDataFromSearchParams, parseFormData } from "remix-hook-form";

import { CustomDrawer } from "~/components/atoms/CustomDrawer";
import { CustomDrawerRef } from "~/components/atoms/CustomDrawer/CustomDrawer";
import Typo from "~/components/atoms/Typo";
import FilterBar, { EFilterBarVariant } from "~/components/molecules/FilterBar";
import FilterBarMobile from "~/components/molecules/FilterBarMobile";
import FilterSideBarOptionsMobile from "~/components/molecules/FilterSideBar/FilterSideBarOptionsMobile/FilterSideBarOptionsMobile";
import ResultHeader from "~/components/molecules/ResultHeader";
import { AutocompleteOption } from "~/components/molecules/SearchBar";
import FilterSideBar from "~/components/organisms/FilterSideBar";
import FilterSideBarMobile from "~/components/organisms/FilterSideBarMobile";
import SearchResult from "~/components/organisms/SearchResult";
import { IProject, IProjectQueries } from "~/entities/project";
import { getAutocompleteProject } from "~/services/autocompleteService";
import {
  getCategory,
  getLocations,
  getUnitTypes,
} from "~/services/commonService";
import { getProjects } from "~/services/projectService";
import { IRouteLoaderData } from "~/types/routeLoaderData";
import { cn } from "~/utilities/cn";
import { REQUEST_KEYS, REQUEST_NAME } from "~/utilities/constants/requestKey";
import { EApiParam } from "~/utilities/enums/ApiParam";
import { ETypoColor } from "~/utilities/enums/Colors";
import { Slug } from "~/utilities/enums/Slug";
import { ETypoTag, ETypoVariant } from "~/utilities/enums/Typo";
import { convertQueryObject } from "~/utilities/helpers/convertQueryObject";
import { getMetadata } from "~/utilities/helpers/metadata";
import { parseSearchParams } from "~/utilities/helpers/queryString";
import { extractDomainFromRequest } from "~/utilities/helpers/requestHelper";
import { TFilterBarFormData } from "~/utilities/schema/filter-bar";
import { TFilterSideBarFormData } from "~/utilities/schema/filterSideBar";

interface IFilterLocation {
  id: string;
  name: string;
}

interface IFilterUnitType {
  id: string;
  name: string;
}

interface IFilterCategory {
  id: string;
  name: string;
}

export type TFilterLoaderData = {
  siteUrl: string;
  projects: IProject[];
  totalProjects: number;
  currentPage: number;
  limit: number;
  filterOptions: {
    locations: IFilterLocation[];
    unitTypes: IFilterUnitType[];
    categories: IFilterCategory[];
  };
};

export const handle: SEOHandle = {
  getSitemapEntries: async () => {
    return [{ route: Slug.PROJECTS, priority: 0.7 }];
  },
};

export const meta: MetaFunction<typeof loader> = (args) => {
  const { data, matches } = args;
  const { siteUrl } = { ...data };

  const rootMatch = matches?.find((match) => match?.id === "root");

  if (!rootMatch) {
    return [];
  }

  const { metadata } = rootMatch.data as IRouteLoaderData;

  return getMetadata({
    title: metadata?.title,
    description: metadata?.description,
    siteUrl,
    image: metadata?.ogImage,
    twitterCreator: metadata?.twitterCreator,
    twitterSite: metadata?.twitterSite,
    additionalMeta: [
      { name: "og:type", content: "website" },
      {
        name: "og:site_name",
        content: metadata?.title,
      },
    ],
  });
};

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const requestUrl = new URL(request?.url || "");
  const domain = extractDomainFromRequest(request);
  const siteUrl =
    requestUrl.protocol + "//" + requestUrl.host + requestUrl.pathname;

  const searchParams = getFormDataFromSearchParams(
    request
  ) as TFilterSideBarFormData & TFilterBarFormData;

  const resUnitTypes = await getUnitTypes(domain);

  const unitTypeOptions = resUnitTypes?.map((unit) => ({
    id: unit.slug,
    bedRoomCount: `${unit?.bedRoomCount || 0}`,
    isStudio: `${unit?.isStudio ? "true" : "false"}`,
    name: unit.title,
  }));

  const tempSearchParams: Partial<IProjectQueries> = convertQueryObject({
    name: searchParams?.s,
    location: searchParams?.location,
    minPrice: searchParams?.rangeMin,
    maxPrice: searchParams?.rangeMax,
    unitType: searchParams.type,
    category: searchParams?.projectType,
    tenure: searchParams?.tenure,
    marketSegment: String(searchParams?.marketSegment).split(","),
    unitMaxSize: searchParams?.unitSizeMax,
    unitMinSize: searchParams?.unitSizeMin,
    minUnitPSF: searchParams?.unitPSFMin,
    maxUnitPSF: searchParams?.unitPSFMax,
    status: String(searchParams?.status).split(","),
    top: searchParams?.top,
    page: searchParams?.page,
    limit: searchParams?.limit,
  });

  if (tempSearchParams["unitType"]) {
    if (
      searchParams["type"] !== "true" &&
      searchParams["type"] !== "false" &&
      isNaN(Number(searchParams["type"]))
    ) {
      tempSearchParams["unitType"] = [searchParams["type"]];
    } else {
      tempSearchParams["unitType"] =
        unitTypeOptions
          ?.filter(
            (option) =>
              option?.bedRoomCount === `${searchParams.type}` ||
              option?.isStudio === `${searchParams?.type?.toString()}`
          )
          ?.map((option) => option?.id) || [];
    }
  }

  const response = await getProjects({
    domain,
    searchParams: !isEmpty(searchParams) ? tempSearchParams : {},
  });

  const resLocations = await getLocations(domain);
  const resCategory = await getCategory(domain);

  const queryParsed = parseSearchParams(requestUrl.searchParams);
  let optionsAutocomplete;

  if (
    queryParsed?.[REQUEST_KEYS.TRIGGER] === REQUEST_NAME.AUTOCOMPLETE_OPTIONS
  ) {
    const response = await getAutocompleteProject({
      domain,
      searchParams: {
        page: queryParsed?.autocompleteParams?.page,
        limit: queryParsed?.autocompleteParams?.limit,
        name: queryParsed?.autocompleteParams?.search,
      },
    });

    const convertedDataAutocomplete: AutocompleteOption[] =
      response?.items?.map((item) => ({
        ...item,
        label: item.projectName,
        value: item.projectSlug,
      }));

    optionsAutocomplete = {
      ...response,
      items: convertedDataAutocomplete,
    };
  }

  return defer({
    siteUrl,
    projects: response.items,
    totalProjects: response.total,
    currentPage: response.currentPage,
    limit: response.limit,
    filterOptions: {
      locations: resLocations?.map((location) => ({
        id: location.slug,
        name: location.name,
      })),
      unitTypes: unitTypeOptions,
      categories: resCategory?.map((category) => ({
        id: category.slug,
        name: category.name,
      })),
    },
    optionsAutocomplete,
  });
};

export async function action({ request }: ActionFunctionArgs) {
  const formData = await parseFormData<Record<string, string>>(request, false);
  const url = new URL(request?.url || "");

  Object.keys(formData)?.forEach((key) => {
    if (
      formData[key] !== "" &&
      formData[key] !== undefined &&
      formData[key] !== null &&
      formData[key] !== "null"
    ) {
      return url.searchParams.set(key, formData[key]);
    }
    return url.searchParams.delete(key);
  });

  const params = url.searchParams.toString();
  return redirect(`${Slug.PROJECTS}?${params}`);
}

export default function Search() {
  const { projects, totalProjects, currentPage, limit } =
    useLoaderData() as TFilterLoaderData;
  const { t } = useTranslation();
  const mastheadRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);
  const drawerRef = useRef<CustomDrawerRef>(null);
  const isMobile = useMediaQuery({ query: "(max-width: 992px)" });
  const [searchProjects, setSearchProjects] = useState<IProject[]>([]);
  const [currentSearchPage, setCurrentSearchPage] = useState<number>(0);
  const location = useLocation();

  const fetcher = useFetcher<typeof loader>();
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get(EApiParam.KEYWORD) || "";

  useEffect(() => {
    if (!fetcher.data || fetcher.state === "loading") {
      return;
    }

    if (fetcher.data) {
      const newItems = (fetcher?.data?.projects || []) as unknown as IProject[];
      setSearchProjects((prevAssets) => [...prevAssets, ...newItems]);
    }
  }, [fetcher.data]);

  useEffect(() => {
    setSearchProjects(projects || []) as unknown as IProject[];
  }, [projects]);

  useEffect(() => {
    setCurrentSearchPage(currentPage);
  }, [currentPage]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const observer = new IntersectionObserver(
        ([entry]) => {
          setIsSticky(!entry.isIntersecting);
        },
        { threshold: 0 }
      );

      if (mastheadRef.current) {
        observer.observe(mastheadRef.current);
      }

      return () => {
        if (mastheadRef.current) {
          observer.unobserve(mastheadRef.current);
        }
      };
    }
  }, []);

  const handleDrawerClose = useCallback(() => {
    drawerRef.current?.onClose();
  }, []);

  const onLoadMore = () => {
    if (
      fetcher.state === "loading" ||
      searchProjects?.length >= totalProjects
    ) {
      return;
    } else {
      setCurrentSearchPage(currentSearchPage + 1);
      const limitPerPage = fetcher?.data?.limit ? fetcher?.data?.limit : limit;
      const query = `?${EApiParam.PAGE}=${currentSearchPage + 1}&${
        EApiParam.LIMIT
      }=${limitPerPage}&${searchParams}`;
      fetcher.load(query);
    }
  };

  return (
    <main key={location.key} className="lg:flex lg:flex-col lg:gap-3">
      <div className="container hidden lg:block lg:px-6 lg:pt-2">
        <FilterBarMobile />
      </div>
      <section
        data-name="projects-search-mashead"
        className={cn("bg-darkGrey", "p-6 md:p-0")}
        ref={mastheadRef}
      >
        <div
          className={cn(
            "flex flex-col gap-3",
            "fixed top-[71px] z-20 -mx-6 w-full lg:top-0",
            isSticky ? "animate-slideBottom" : "hidden"
          )}
        >
          <FilterBar variant={EFilterBarVariant.STICKY} />
        </div>

        <div className={cn("flex flex-col gap-3", "container wrapper")}>
          <FilterBar variant={EFilterBarVariant.DEFAULT} />
        </div>
      </section>
      <section
        data-name="projects-search"
        className="flex flex-col gap-6 py-12 lg:pt-0"
      >
        <div className="flex flex-col justify-between gap-6">
          <div className="container flex-1">
            {isMobile ? (
              <CustomDrawer
                ref={drawerRef}
                side={
                  <FilterSideBarMobile
                    mode="onSubmit"
                    onClose={handleDrawerClose}
                  />
                }
                actions={<div className="h-10"></div>}
                title={
                  <div className="flex flex-1 flex-row items-center justify-between pr-6">
                    <Typo
                      color={ETypoColor.TEXT}
                      tag={ETypoTag.P}
                      variant={ETypoVariant.HEADER_24}
                    >
                      {t("filter")}
                    </Typo>
                    <Link to={Slug.PROJECTS}>
                      <Typo
                        tag={ETypoTag.P}
                        variant={ETypoVariant.BODY_TITLE_16}
                        color={ETypoColor.ERROR}
                        className="cursor-pointer"
                      >
                        {t("clear_all")}
                      </Typo>
                    </Link>
                  </div>
                }
              >
                <FilterSideBarOptionsMobile href={Slug.PROJECTS} />
              </CustomDrawer>
            ) : (
              <div className="flex px-6 lg:hidden">
                <FilterSideBar mode="onSubmit" />
              </div>
            )}
          </div>
          <ResultHeader
            title="Result"
            totalResult={totalProjects ?? 0}
            containerClassName="hidden lg:flex mb-0 flex-1"
          />
        </div>
        <SearchResult
          className="container"
          title={
            <ResultHeader
              containerClassName="lg:hidden flex-1"
              title="Result"
              totalResult={totalProjects ?? 0}
            />
          }
          projects={searchProjects}
          onLoadMore={onLoadMore}
        />
      </section>
    </main>
  );
}
